import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { Activity } from '@vendasta/crm';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { CrmFieldService, StandardIds } from '@galaxy/crm/static';
import { format as formatDate } from 'date-fns-tz';
import { MatInputModule } from '@angular/material/input';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateModule } from '@ngx-translate/core';

export interface RescheduleEvent {
  dueDate: Date;
  changed: boolean;
}

@Component({
  selector: 'crm-reschedule-task',
  templateUrl: 'reschedule-task.component.html',
  styleUrls: ['reschedule-task.component.scss'],
  imports: [
    CommonModule,
    GalaxyFormFieldModule,
    GalaxyPopoverModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDatepickerModule,
    TranslateModule,
  ],
})
export class CrmRescheduleTaskComponent {
  private readonly fieldService = inject(CrmFieldService);
  private readonly snackbarService = inject(SnackbarService);

  protected readonly PopoverPositionBottom = PopoverPositions['Bottom'];
  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected isOpen$ = this.isOpen$$.asObservable();

  private _task: Activity | null = null;
  @Input() set task(value: Activity | null) {
    this._task = value;
    this.closePopover();
  }
  get task(): Activity | null {
    return this._task;
  }

  @Output() handleReschedule = new EventEmitter<RescheduleEvent>();

  protected rescheduleFormControl = new FormControl<string>(
    {
      value: '',
      disabled: true,
    },
    Validators.required,
  );

  constructor() {
    this.isOpen$
      .pipe(
        takeUntilDestroyed(),
        filter((isOpen) => isOpen),
      )
      .subscribe(() => {
        if (this.task) {
          const dueDate = this.fieldService.getFieldValueFromCrmObject(
            this.task,
            StandardIds.ActivityTaskDueDate,
          )?.dateValue;
          this.rescheduleFormControl.setValue(dueDate ? formatDate(dueDate, "yyyy-MM-dd'T'HH:mm") : '');
          this.rescheduleFormControl.markAsPristine();
          this.rescheduleFormControl.enable();
        }
      });
  }

  togglePopover() {
    firstValueFrom(this.isOpen$).then((isOpen) => this.isOpen$$.next(!isOpen));
  }

  closePopover() {
    this.isOpen$$.next(false);
  }

  async handleRescheduleTaskClick(): Promise<void> {
    const hasChanged = this.rescheduleFormControl.dirty;
    const hasError = this.rescheduleFormControl.invalid;
    const newDueDate = this.rescheduleFormControl.value ? new Date(this.rescheduleFormControl.value) : undefined;
    if (hasError || !newDueDate) {
      this.snackbarService.openErrorSnack('TASK_QUEUE.ERRORS.INVALID_DUE_DATE');
      return;
    }

    this.handleReschedule.emit({
      dueDate: newDueDate,
      changed: hasChanged,
    });
  }
}
